/**
 * Conversation metadata encryption with a random per-conversation key.
 *
 * Group names, member settings, read-state, and system events used to be
 * HKDF(conversationId) or plaintext. The server creates conversation IDs, so
 * HKDF was only obfuscation. Callers wrap the random key to each attested
 * device (same pin/attest path as messages).
 *
 * @module crypto/dm/metadata
 */
import { toBytes, toBase64, fromBase64, concatBytes } from '../utils';
import { encrypt, decrypt } from '../encrypt';
export const CONV_METADATA_AAD_DOMAIN = 'adieuu-conv-meta-aad-v1';
const NONCE_SIZE = 12;
export function buildConversationMetadataAad(conversationId, purpose) {
    if (!conversationId) {
        throw new Error('conversationId is required for metadata AAD');
    }
    return toBytes(`${CONV_METADATA_AAD_DOMAIN}:${purpose}:${conversationId}`);
}
export function encryptConversationMetadataField(key, plaintext, conversationId, purpose, profile = 'default') {
    const aad = buildConversationMetadataAad(conversationId, purpose);
    return encrypt(key, plaintext, profile, undefined, aad);
}
export function decryptConversationMetadataField(key, ciphertext, nonce, conversationId, purpose, profile = 'default') {
    const aad = buildConversationMetadataAad(conversationId, purpose);
    return decrypt(key, ciphertext, nonce, profile, aad);
}
/**
 * Encrypts lastReadMessageId with a conversation metadata key.
 * Blob format matches legacy HKDF encryptReadState (nonce || ciphertext, base64).
 */
export function encryptReadStateWithKey(key, messageId, conversationId, profile = 'default') {
    const { ciphertext, nonce } = encryptConversationMetadataField(key, toBytes(messageId), conversationId, 'read-state', profile);
    return toBase64(concatBytes(nonce, ciphertext));
}
export function decryptReadStateWithKey(key, encryptedBlob, conversationId, profile = 'default') {
    try {
        const combined = fromBase64(encryptedBlob);
        const nonce = combined.slice(0, NONCE_SIZE);
        const ciphertext = combined.slice(NONCE_SIZE);
        const plaintext = decryptConversationMetadataField(key, ciphertext, nonce, conversationId, 'read-state', profile);
        return new TextDecoder().decode(plaintext);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=metadata.js.map